/**
 * Billing prompts outside Settings (the approved billing design, section 4): the one banner across the top of the app
 * (payment failed > read-only > trial; core `logic/billing#chromeBanner`), the composer's read-only notice, and the
 * invite sheet's member-limit prompt. Owners and admins get a button; members get a name to ask.
 */
import type { BillingBanner as BannerState, BillingScreenState, BillingViewModel, InviteRoom, StorageBanner } from "@bundle/core";
import { Banner } from "../../primitives/index.ts";
import { useT } from "../../i18n/I18nProvider.tsx";
import { ComposerNotice } from "../Composer.tsx";
import { useBillingFormat, useManagerNames } from "./format.ts";

/** Title and message for a billing banner (shared by the app chrome and the Billing page). */
export function useBannerCopy(): (b: BannerState, state: BillingScreenState, workspaceName: string) => { title: string; message: string } {
  const t = useT();
  const f = useBillingFormat();
  const names = useManagerNames();
  return (b, state, ws) => {
    if (b.kind === "pastDue") {
      return {
        title: t("billing.pastDue.banner.title"),
        message: b.graceEndsAt === undefined ? t("billing.pastDue.banner.body", { ws }) : t("billing.pastDue.banner.until", { ws, date: f.date(b.graceEndsAt) }),
      };
    }
    if (b.kind === "locked") {
      const ended = b.reason === "trial" && state.billing?.trialEndsAt !== undefined ? t("billing.readOnly.trialEnded", { date: f.date(state.billing.trialEndsAt) }) : t("billing.readOnly.ended");
      return { title: `${t("billing.readOnly", { ws })}.`, message: b.tone === "neutral" ? t("billing.askAdmins", { names }) : ended };
    }
    const title = t("billing.trial.banner.title", { n: b.daysLeft });
    if (b.action === undefined && b.tone === "danger") return { title, message: t("billing.trial.banner.member", { ws, names }) };
    const date = f.date(b.trialEndsAt);
    const message = b.tone === "info" ? t("billing.trial.banner.info", { ws, date }) : b.tone === "warning" ? t("billing.trial.banner.warning", { ws, date }) : t("billing.trial.banner.danger");
    return { title, message };
  };
}

/** The action button for a banner: Choose a plan / Subscribe (the picker) or Update payment method (the portal). */
export function useBannerAction(vm: BillingViewModel, b: BannerState, testID: string): { label: string; onPress: () => void; testID: string; variant?: "primary" | "secondary" } | undefined {
  const t = useT();
  if (b.action === undefined) return undefined;
  if (b.action === "updateCard") return { label: t("billing.updateCard"), onPress: () => void vm.portal(), testID: `${testID}-action`, variant: "primary" };
  // Primary only at 3 and 1 days and when locked, when acting is the point (the 7-day one is secondary).
  const primary = b.kind === "locked" || b.tone !== "info";
  return { label: b.action === "subscribe" ? t("billing.subscribe") : t("billing.choosePlan"), onPress: () => vm.openPicker(), testID: `${testID}-action`, ...(primary ? { variant: "primary" as const } : {}) };
}

const ICON = { pastDue: "alert", locked: "lock", trial: "hourglass" } as const;

/** One billing banner. `placement` "edge" in the app chrome (full width under the titlebar), "inline" on the Billing page. */
export function BillingBannerView({
  vm,
  state,
  banner,
  workspaceName,
  placement,
  testID,
}: {
  vm: BillingViewModel;
  state: BillingScreenState;
  banner: BannerState;
  workspaceName: string;
  placement: "edge" | "inline";
  testID: string;
}) {
  const t = useT();
  const copy = useBannerCopy()(banner, state, workspaceName);
  const action = useBannerAction(vm, banner, testID);
  const dismissible = banner.kind === "trial" && banner.dismissible;
  return (
    <Banner
      tone={banner.tone}
      icon={ICON[banner.kind]}
      title={copy.title}
      message={copy.message}
      placement={placement}
      {...(action === undefined ? {} : { action })}
      {...(dismissible ? { onDismiss: () => vm.dismissBanner(), dismissLabel: t("common.dismiss") } : {})}
      testID={testID}
    />
  );
}

/** The composer's slot in a read-only workspace: what happened, and Subscribe for owners and admins. */
export function BillingComposerNotice({ vm, state, testID }: { vm: BillingViewModel; state: BillingScreenState; testID: string }) {
  const t = useT();
  const names = useManagerNames();
  const manage = state.view?.manage === true;
  return (
    <ComposerNotice
      title={t("billing.composer.title")}
      message={manage ? t("billing.composer.admin") : t("billing.composer.member", { names })}
      {...(manage ? { action: { label: t("billing.subscribe"), onPress: () => vm.openPicker(), testID: `${testID}-subscribe` } } : {})}
      testID={testID}
    />
  );
}

/** The invite sheet's member-limit prompt (fewer seats left than invitations entered, or none left). */
export function InviteLimitBanner({ room, manage, workspaceName, onContact, testID }: { room: InviteRoom; manage: boolean; workspaceName: string; onContact?: () => void; testID: string }) {
  const t = useT();
  const names = useManagerNames();
  const ws = workspaceName;
  const contact = manage && onContact !== undefined ? { action: { label: t("billing.contactPro"), onPress: onContact, testID: `${testID}-contact` } } : {};
  if (room.kind === "over") {
    return (
      <Banner
        tone="warning"
        icon="users"
        title={t("billing.invite.over.title", { left: room.left })}
        message={t("billing.invite.over.body", { limit: room.limit, ws, members: room.members, remove: room.remove })}
        {...contact}
        testID={testID}
      />
    );
  }
  return manage ? (
    <Banner tone="danger" icon="users" title={t("billing.invite.full.title", { ws, members: room.members, limit: room.limit })} message={t("billing.invite.full.admin")} {...contact} testID={testID} />
  ) : (
    <Banner tone="danger" icon="users" title={t("billing.invite.full.memberTitle", { ws })} message={t("billing.invite.full.member", { limit: room.limit, names })} testID={testID} />
  );
}

const STORE_NAME = { app_store: "App Store", play: "Google Play" } as const;

/**
 * The storage warning in the app chrome (core `StorageLimit.storageBanner`): 80 % for owners and admins (warning,
 * dismissible for a day), 95 % and full for everyone (danger). Owners and admins get the storage-full prompt's button.
 */
export function StorageBannerView({ vm, banner, workspaceName, onContact, testID }: { vm: BillingViewModel; banner: StorageBanner; workspaceName: string; onContact?: () => void; testID: string }) {
  const t = useT();
  const f = useBillingFormat();
  const names = useManagerNames();
  const ws = workspaceName;
  const used = f.gb(banner.usedBytes);
  const pool = f.gb(banner.poolBytes);
  const title = banner.level === "full" ? t("billing.storageWarn.fullTitle", { ws }) : t("billing.storageWarn.title", { ws, percent: banner.percent });
  const message = !banner.manage
    ? t("billing.storageWarn.member", { used, pool, names })
    : banner.store !== undefined
      ? t("billing.storageWarn.store", { used, pool, store: STORE_NAME[banner.store] })
      : banner.action === "contact"
        ? t("billing.storageWarn.contact", { used, pool, ws })
        : banner.action === "addStorage"
          ? t("billing.storageWarn.addStorage", { used, pool })
          : t("billing.storageWarn.choosePlan", { used, pool });
  const a = banner.action;
  const action =
    a === undefined || (a === "contact" && onContact === undefined)
      ? undefined
      : {
          label: a === "addStorage" ? t("billing.addStorage") : a === "choosePlan" ? t("billing.choosePlan") : t("billing.contactPro"),
          onPress: () => (a === "contact" ? onContact?.() : vm.storageBannerAction()),
          testID: `${testID}-action`,
          ...(banner.tone === "danger" ? { variant: "primary" as const } : {}),
        };
  return (
    <Banner
      tone={banner.tone}
      icon="hardDrive"
      title={title}
      message={message}
      placement="edge"
      {...(action === undefined ? {} : { action })}
      {...(banner.dismissible ? { onDismiss: () => vm.dismissStorageBanner(), dismissLabel: t("common.dismiss") } : {})}
      testID={testID}
    />
  );
}
